/**
 * AdminPanel Component
 * Provides content management interface for ologies and apps
 */

import './AdminPanel.css';

class AdminPanel {
  constructor(container) {
    this.container = container;
    this.token = localStorage.getItem('adminToken');
    this.activeTab = 'ologies';
    this.searchTerm = '';
    this.ologies = [];
    this.apps = [];
    this.showCreateModal = false;
    this.createType = '';
    this.editingItem = null;
    
    this.appTemplate = `---
title: ""
subtitle: ""
description: ""
type: "mobile"
appStoreUrl: ""
websiteUrl: ""
appIconUrl: ""
screenshotUrls:
  - ""
screenshotData:
  - title: ""
    description: ""
    alt: ""
rating: 5
category: ""
status: "live"
publishDate: ${new Date().toISOString().split('T')[0]}
tags: []
featured: false
author: "Our Appologies Team"
seoTitle: ""
socialImage: ""
cardImage: ""
cardTextColor: "white"
---

# Your App Title

Your app content goes here...`;

    this.ologyTemplate = `---
title: ""
type: "manifesto"
description: ""
publishDate: ${new Date().toISOString().split('T')[0]}
tags: []
featured: false
author: ""
duration: ""
difficulty: ""
prerequisites: []
relatedContent: []
seoTitle: ""
socialImage: ""
cardImage: ""
cardTextColor: "white"
---

# Your Ology Title

Your ology content goes here...`;
  }

  async render() {
    // Set up the container for admin content
    this.container.className = 'admin-container';
    
    if (!this.token) {
      this.showLogin();
      return;
    }

    try {
      const isValid = await this.verifyToken();
      if (!isValid) {
        this.showLogin();
        return;
      }
      
      await this.loadContent();
      this.renderDashboard();
    } catch (error) {
      console.error('Init error:', error);
      this.showLogin();
    }
  }

  async init() {
    // Legacy method for backwards compatibility
    await this.render();
  }

  async verifyToken() {
    try {
      const response = await fetch('/api/admin/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'verify', token: this.token })
      });
      const data = await response.json();
      return data.valid;
    } catch (error) {
      console.error('Token verification failed:', error);
      return false;
    }
  }

  async loadContent() {
    try {
      const [ologiesRes, appsRes] = await Promise.all([
        fetch('/file/ologies/api/content/ologies'),
        fetch('/file/ologies/api/content/apps')
      ]);

      this.ologies = await ologiesRes.json();
      this.apps = await appsRes.json();
    } catch (error) {
      console.error('Failed to load content:', error);
      this.ologies = [];
      this.apps = [];
    }
  }

  showLogin() {
    this.container.innerHTML = `
      <div class="login-container">
        <div class="terminal-window">
          <div class="terminal-header">
            <span class="terminal-title">ADMIN ACCESS REQUIRED</span>
          </div>
          <div class="terminal-content">
            <div class="login-form">
              <div class="input-group">
                <label>PASSWORD:</label>
                <input type="password" id="admin-password" placeholder="Enter admin password">
              </div>
              <button id="login-btn" class="primary-btn">ACCESS GRANTED</button>
            </div>
          </div>
        </div>
      </div>
    `;

    setTimeout(() => {
      const loginBtn = this.container.querySelector('#login-btn');
      const passwordInput = this.container.querySelector('#admin-password');
      
      if (loginBtn && passwordInput) {
        loginBtn.addEventListener('click', () => this.login());
        passwordInput.addEventListener('keypress', (e) => {
          if (e.key === 'Enter') this.login();
        });
        passwordInput.focus();
      }
    }, 100);
  }

  async login() {
    const password = this.container.querySelector('#admin-password')?.value;
    if (!password) return;

    try {
      const response = await fetch('/api/admin/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });

      const data = await response.json();
      
      if (data.success) {
        this.token = data.token;
        localStorage.setItem('adminToken', this.token);
        await this.loadContent();
        this.renderDashboard();
      } else {
        alert('ACCESS DENIED');
      }
    } catch (error) {
      console.error('Login failed:', error);
      alert('LOGIN ERROR');
    }
  }

  renderDashboard() {
    const filteredOlogies = this.filterContent(this.ologies);
    const filteredApps = this.filterContent(this.apps);

    this.container.innerHTML = `
      <div class="dashboard-container">
        <div class="dashboard-header">
          <h1>CONTENT MANAGEMENT SYSTEM</h1>
          <div class="header-actions">
            <button id="test-github-btn" class="secondary-btn">TEST GITHUB</button>
            <button id="logout-btn" class="secondary-btn">LOGOUT</button>
          </div>
        </div>

        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-number">${this.ologies.length}</div>
            <div class="stat-label">TOTAL OLOGIES</div>
          </div>
          <div class="stat-card">
            <div class="stat-number">${this.apps.length}</div>
            <div class="stat-label">TOTAL APPS</div>
          </div>
          <div class="stat-card">
            <div class="stat-number">${this.ologies.filter(o => o.data.featured).length}</div>
            <div class="stat-label">FEATURED OLOGIES</div>
          </div>
          <div class="stat-card">
            <div class="stat-number">${this.apps.filter(a => a.data.featured).length}</div>
            <div class="stat-label">FEATURED APPS</div>
          </div>
        </div>

        <div class="content-controls">
          <div class="tabs">
            <button class="tab-btn ${this.activeTab === 'ologies' ? 'active' : ''}" data-tab="ologies">
              OLOGIES (${filteredOlogies.length})
            </button>
            <button class="tab-btn ${this.activeTab === 'apps' ? 'active' : ''}" data-tab="apps">
              APPS (${filteredApps.length})
            </button>
          </div>

          <div class="controls-row">
            <div class="search-container">
              <input type="text" id="search-input" placeholder="Search content..." value="${this.searchTerm}">
            </div>
            <div class="create-dropdown">
              <button id="create-btn" class="primary-btn">CREATE ▼</button>
              <div id="create-menu" class="dropdown-menu">
                <button data-type="ology">Create New Ology</button>
                <button data-type="app">Create New App</button>
              </div>
            </div>
          </div>
        </div>

        <div class="content-table">
          ${this.renderContentTable()}
        </div>
      </div>

      ${this.showCreateModal ? this.renderCreateModal() : ''}
    `;

    this.attachEventListeners();
  }

  renderContentTable() {
    const content = this.activeTab === 'ologies' ? this.filterContent(this.ologies) : this.filterContent(this.apps);
    
    if (content.length === 0) {
      return `<div class="empty-state">No ${this.activeTab} found</div>`;
    }

    return `
      <table class="data-table">
        <thead>
          <tr>
            <th>TITLE</th>
            <th>PUBLISH DATE</th>
            <th>TYPE</th>
            <th>FEATURED</th>
            <th>ACTIONS</th>
          </tr>
        </thead>
        <tbody>
          ${content.map(item => this.renderTableRow(item)).join('')}
        </tbody>
      </table>
    `;
  }

  renderTableRow(item) {
    const slug = item.slug || item.data.title.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');
    const viewUrl = this.activeTab === 'ologies' ? `/ologies/${slug}` : `/apps/${slug}`;
    
    return `
      <tr>
        <td class="title-cell">
          <div class="title">${item.data.title || 'Untitled'}</div>
          <div class="subtitle">${item.data.subtitle || item.data.description || ''}</div>
        </td>
        <td>${item.data.publishDate || 'N/A'}</td>
        <td><span class="type-badge">${item.data.type || this.activeTab.slice(0, -1)}</span></td>
        <td><span class="featured-badge ${item.data.featured ? 'featured' : ''}">${item.data.featured ? 'YES' : 'NO'}</span></td>
        <td class="actions-cell">
          <button class="action-btn view-btn" data-action="view" data-url="${viewUrl}">VIEW</button>
          <button class="action-btn edit-btn" data-action="edit" data-slug="${slug}">EDIT</button>
          <button class="action-btn delete-btn" data-action="delete" data-slug="${slug}">DELETE</button>
        </td>
      </tr>
    `;
  }

  renderCreateModal() {
    const isEdit = this.editingItem !== null;
    const template = this.editingItem ? this.editingItem.content : 
                    (this.createType === 'app' ? this.appTemplate : this.ologyTemplate);
    const title = isEdit ? `EDIT ${this.createType.toUpperCase()}` : `CREATE NEW ${this.createType.toUpperCase()}`;
    const submitText = isEdit ? `UPDATE ${this.createType.toUpperCase()}` : `CREATE ${this.createType.toUpperCase()}`;
    
    return `
      <div class="modal-overlay">
        <div class="modal-container">
          <div class="modal-header">
            <h2>${title}</h2>
            <button id="close-modal" class="close-btn">×</button>
          </div>
          <div class="modal-content">
            <div class="form-group">
              <label>MDX CONTENT:</label>
              <textarea id="mdx-content" class="mdx-editor" rows="25">${template}</textarea>
            </div>
            <div class="modal-actions">
              <button id="cancel-create" class="secondary-btn">CANCEL</button>
              <button id="submit-create" class="primary-btn">${submitText}</button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  filterContent(content) {
    if (!this.searchTerm) return content;
    
    return content.filter(item => 
      item.data.title.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
      (item.data.description && item.data.description.toLowerCase().includes(this.searchTerm.toLowerCase())) ||
      (item.data.tags && item.data.tags.some(tag => tag.toLowerCase().includes(this.searchTerm.toLowerCase())))
    );
  }

  attachEventListeners() {
    setTimeout(() => {
      // Logout
      this.container.querySelector('#logout-btn')?.addEventListener('click', () => {
        localStorage.removeItem('adminToken');
        location.reload();
      });

      // Test GitHub
      this.container.querySelector('#test-github-btn')?.addEventListener('click', () => {
        this.testGitHub();
      });

      // Tab switching
      this.container.querySelectorAll('.tab-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
          this.activeTab = e.target.dataset.tab;
          this.renderDashboard();
        });
      });

      // Search
      this.container.querySelector('#search-input')?.addEventListener('input', (e) => {
        this.searchTerm = e.target.value;
        this.renderDashboard();
      });

      // Create dropdown
      this.container.querySelector('#create-btn')?.addEventListener('click', () => {
        const menu = this.container.querySelector('#create-menu');
        menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
      });

      this.container.querySelectorAll('#create-menu button').forEach(btn => {
        btn.addEventListener('click', (e) => {
          this.createType = e.target.dataset.type;
          this.showCreateModal = true;
          this.renderDashboard();
        });
      });

      // Table actions
      this.container.querySelectorAll('.action-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const action = e.target.dataset.action;
          const slug = e.target.dataset.slug;
          const url = e.target.dataset.url;

          switch (action) {
            case 'view':
              window.open(url, '_blank');
              break;
            case 'edit':
              this.editContent(slug);
              break;
            case 'delete':
              this.deleteContent(slug);
              break;
          }
        });
      });

      // Modal actions
      this.container.querySelector('#close-modal')?.addEventListener('click', () => {
        this.showCreateModal = false;
        this.editingItem = null;
        this.renderDashboard();
      });

      this.container.querySelector('#cancel-create')?.addEventListener('click', () => {
        this.showCreateModal = false;
        this.editingItem = null;
        this.renderDashboard();
      });

      this.container.querySelector('#submit-create')?.addEventListener('click', () => {
        if (this.editingItem) {
          this.submitEdit();
        } else {
          this.submitCreate();
        }
      });

      // Close dropdown when clicking outside
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.create-dropdown')) {
          this.container.querySelector('#create-menu').style.display = 'none';
        }
      });
    }, 100);
  }

  async testGitHub() {
    try {
      const response = await fetch('/api/admin/test-github', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        alert(`GitHub Test Successful!\n\nRepo: ${data.repo.fullName}\nFiles found: ${data.contentsAccess.fileCount}\nToken: ${data.tokenInfo.tokenPrefix}`);
      } else {
        alert(`GitHub Test Failed!\n\nError: ${data.error}\nDetails: ${JSON.stringify(data.details, null, 2)}`);
      }
    } catch (error) {
      console.error('GitHub test failed:', error);
      alert('GitHub test failed: ' + error.message);
    }
  }

  async editContent(slug) {
    try {
      // Load the complete raw MDX file content including frontmatter
      const endpoint = this.activeTab === 'ologies' ? 
                      `/api/admin/get-raw-ology?slug=${slug}` : 
                      `/api/admin/get-raw-app?slug=${slug}`;
      
      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load content');
      }
      
      const contentData = await response.json();
      
      // Convert activeTab to proper singular form
      const itemType = this.activeTab === 'ologies' ? 'ology' : 'app';
      
      this.editingItem = {
        slug: slug,
        content: contentData.content,
        type: itemType
      };
      this.createType = itemType;
      this.showCreateModal = true;
      this.renderDashboard();
      
    } catch (error) {
      console.error('Failed to load content for editing:', error);
      alert('Failed to load content for editing: ' + error.message);
    }
  }

  async deleteContent(slug) {
    // Convert activeTab to proper singular form
    const itemType = this.activeTab === 'ologies' ? 'ology' : 'app';
    
    const confirmed = confirm(`Are you sure you want to delete this ${itemType}? This action cannot be undone.`);
    if (!confirmed) return;

    try {
      const response = await fetch(`/api/admin/delete-${itemType}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.token}`
        },
        body: JSON.stringify({ slug })
      });

      const data = await response.json();
      
      if (data.success) {
        await this.loadContent();
        this.renderDashboard();
        
        const message = data.deployTriggered 
          ? `Content deleted successfully! 🚀 Deployment triggered - site will update in ~2-3 minutes.`
          : `Content deleted successfully! Deployment will start automatically.`;
          
        alert(message);
      } else {
        alert('Failed to delete content: ' + data.error);
      }
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Delete failed');
    }
  }

  async submitCreate() {
    const content = this.container.querySelector('#mdx-content')?.value;
    if (!content) return;

    try {
      const endpoint = this.createType === 'app' ? '/api/admin/create-app' : '/api/admin/create-ology';
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.token}`
        },
        body: JSON.stringify({ content })
      });

      const data = await response.json();
      
      if (data.success) {
        this.showCreateModal = false;
        this.editingItem = null;
        await this.loadContent();
        this.renderDashboard();
        
        const message = data.deployTriggered 
          ? `${this.createType} created successfully! 🚀 Deployment triggered - site will update in ~2-3 minutes.`
          : `${this.createType} created successfully! Deployment will start automatically.`;
          
        alert(message);
      } else {
        alert('Failed to create content: ' + data.error);
      }
    } catch (error) {
      console.error('Create failed:', error);
      alert('Create failed');
    }
  }

  async submitEdit() {
    const content = this.container.querySelector('#mdx-content')?.value;
    if (!content) return;

    const itemType = this.editingItem.type;
    const itemSlug = this.editingItem.slug;

    try {
      const endpoint = `/api/admin/update-${itemType}`;
      
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.token}`
        },
        body: JSON.stringify({ slug: itemSlug, content })
      });

      const data = await response.json();
      
      if (data.success) {
        this.showCreateModal = false;
        this.editingItem = null;
        await this.loadContent();
        this.renderDashboard();
        
        const message = data.deployTriggered 
          ? `${itemType} updated successfully! 🚀 Deployment triggered - site will update in ~2-3 minutes.`
          : `${itemType} updated successfully! Deployment will start automatically.`;
          
        alert(message);
      } else {
        alert('Failed to update content: ' + data.error);
        console.error('Update error details:', data);
      }
    } catch (error) {
      console.error('Update failed:', error);
      alert('Update failed');
    }
  }
}

export default AdminPanel;